import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const SkillsMatrix = () => {
  const [animatedValues, setAnimatedValues] = useState({});

  const skillCategories = [
    {
      category: "Technical Skills",
      icon: "Code2",
      color: "var(--color-primary)",
      skills: [
        { name: "React & Next.js", level: 95 },
        { name: "Node.js & Express", level: 90 },
        { name: "Cloud Architecture (AWS/Azure)", level: 85 },
        { name: "Database Design (SQL/NoSQL)", level: 88 },
        { name: "DevOps & CI/CD", level: 82 },
        { name: "Mobile Development (React Native)", level: 80 }
      ]
    },
    {
      category: "Business & Strategy",
      icon: "TrendingUp",
      color: "var(--color-secondary)",
      skills: [
        { name: "Business Strategy & Planning", level: 92 },
        { name: "Digital Transformation", level: 88 },
        { name: "Market Analysis & Research", level: 85 },
        { name: "Financial Management", level: 80 },
        { name: "Team Leadership", level: 90 },
        { name: "Project Management", level: 87 }
      ]
    },
    {
      category: "Creative Direction",
      icon: "Palette",
      color: "var(--color-accent)",
      skills: [
        { name: "Brand Identity Design", level: 90 },
        { name: "UI/UX Design", level: 88 },
        { name: "Visual Communication", level: 92 },
        { name: "Content Strategy", level: 85 },
        { name: "Creative Consulting", level: 87 },
        { name: "Music Production (Ed Pulse)", level: 83 }
      ]
    }
  ];

  useEffect(() => {
    const timer = setTimeout(() => {
      const values = {};
      skillCategories?.forEach(category => {
        category?.skills?.forEach(skill => {
          values[skill.name] = skill?.level;
        });
      });
      setAnimatedValues(values);
    }, 300);

    return () => clearTimeout(timer);
  }, []);

  return (
    <section className="py-16 lg:py-24 bg-background">
      <div className="container mx-auto px-4 md:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 lg:mb-16"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full mb-4">
            <Icon name="Target" size={18} color="var(--color-primary)" />
            <span className="text-sm font-medium text-primary">Expertise</span>
          </div>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4">Skills & Competencies</h2>
          <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
            A comprehensive overview of my technical abilities, business acumen, and creative capabilities
          </p>
        </motion.div>

        <div className="space-y-8 lg:space-y-12">
          {skillCategories?.map((category, categoryIndex) => (
            <motion.div
              key={categoryIndex}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: categoryIndex * 0.1, duration: 0.6 }}
              className="bg-card rounded-xl lg:rounded-2xl p-6 lg:p-8 border border-border"
            >
              <div className="flex items-center gap-4 mb-6 lg:mb-8">
                <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${category?.color}15` }}>
                  <Icon name={category?.icon} size={28} color={category?.color} />
                </div>
                <h3 className="text-xl md:text-2xl font-semibold">{category?.category}</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
                {category?.skills?.map((skill, skillIndex) => (
                  <motion.div
                    key={skillIndex}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: skillIndex * 0.05, duration: 0.4 }}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm md:text-base font-medium">{skill?.name}</span>
                      <span className="text-sm font-semibold" style={{ color: category?.color }}>
                        {animatedValues?.[skill?.name] || 0}%
                      </span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${animatedValues?.[skill?.name] || 0}%` }}
                        transition={{ duration: 1, delay: skillIndex * 0.05, ease: "easeOut" }}
                        className="h-full rounded-full"
                        style={{ backgroundColor: category?.color }}
                      />
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default SkillsMatrix;